import { MATERIAL_ICONS_BASE } from '../constants'
import { SvgIconComponent } from '@mui/icons-material'
import { createSvgIcon } from '@mui/material/utils'
import {
  fetchDynamicMuiIconReducer,
  initialFetchDynamicMuiIconReducerState
} from '../utils/fetchDynamicMuiIconReducer'
import { pascalCase, snakeCase } from 'scule'
import { useCallback, useEffect, useMemo, useReducer } from 'react'
import parse, {
  DOMNode,
  Element,
  HTMLReactParserOptions,
  attributesToProps,
  domToReact
} from 'html-react-parser'

interface UseLoadDynamicMuiIcon {
  isLoading: boolean
  error?: string | null
  SvgElement: SvgIconComponent | null
}

export const useLoadDynamicMuiIcon = (icon: string): UseLoadDynamicMuiIcon => {
  const iconPathName = snakeCase(icon)
  const iconComponentName = pascalCase(iconPathName)
  const url = `${MATERIAL_ICONS_BASE}/${iconPathName}_24px.svg`

  const [{ isLoading, data, error }, dispatch] = useReducer(
    fetchDynamicMuiIconReducer,
    initialFetchDynamicMuiIconReducerState
  )

  // Fetch the SVG content from the cache or the network
  const getFromCacheOrFetch = useCallback(async (): Promise<string> => {
    const req = new Request(url)

    // If the browser doesn't support the Cache API, or we're running this on the server, just fetch the data
    try {
      if (typeof caches === 'undefined') throw new Error('Cache API not supported.')
      const cache = await caches.open('dynamic-icons')
      const cachedResponse = await cache.match(req) // returns undefined if not found in cache
      if (cachedResponse) {
        return cachedResponse.text()
      } else {
        const fetchedResponse = await fetch(req)
        if (!fetchedResponse.ok) throw new Error('Network response was not ok.')
        const cacheableResponse = fetchedResponse.clone()
        await cache.put(req, cacheableResponse)
        return fetchedResponse.text()
      }
    } catch {
      return fetch(req).then(res => res.text())
    }
  }, [url])

  // Fetch the SVG content
  useEffect(() => {
    if (data) return
    dispatch({ type: 'LOADING' })

    getFromCacheOrFetch()
      .then(data => {
        dispatch({ type: 'SUCCESS', payload: data })
      })
      .catch(error => {
        dispatch({ type: 'ERROR', payload: error })
      })
  }, [data, getFromCacheOrFetch])

  // Create the <svg> as a JSX element using `html-react-parser` and
  // and pass it to the createSvgIcon function to create an MUI icon component
  const SvgElement = useMemo(() => {
    if (!data) return null
    const options: HTMLReactParserOptions = {
      replace(domNode) {
        if (domNode instanceof Element && domNode.attribs && domNode.name === 'svg') {
          const props = attributesToProps(domNode.attribs)
          return (
            <svg {...props} fill="currentColor">
              {domToReact(domNode.children as DOMNode[], options)}
            </svg>
          )
        }
      }
    }
    const svgElement = parse(data, options)
    return createSvgIcon(svgElement, iconComponentName)
  }, [data, iconComponentName])

  return {
    isLoading: isLoading,
    error: error,
    SvgElement
  }
}
